import { getStateOptions } from '@/helpers/getStateOptions';
import { getUserFacingErrorMessage } from '@/services/helpers';
import { type PromoRow, useCreatePromoMutation, useUpdatePromoMutation } from '@/services/promos';
import {
  PalmettoButton,
  PalmettoChip,
  PalmettoModal,
  PalmettoSelect,
  PalmettoTextInput,
} from '@palmetto/dsm-components';
import { DateInput, toast } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { PromoParty, PromoType, PromoUtility, PromoValueType } from 'types';

import { programTypeOptions } from '../equipment-manufacturers/equipmentLabels';
import { SearchableMultiSelect } from '../shared/SearchableMultiSelect';

import {
  defaultPromoFormValues,
  fromIsoDateString,
  promoToFormValues,
  type PromoFormValues,
  toCreatePromoRequest,
  toIsoDateString,
  toUpdatePromoRequest,
  validateCollectableBy,
  validateLseIds,
  validatePromoType,
  validateProgramTypes,
  validateStates,
  validateValidThrough,
  validateValue,
  validateValueType,
} from './promoFormModel';
import { usePromoAvailableStates, usePromoUtilityOptions } from './usePromoUtilityOptions';

const allStateOptions = getStateOptions().map(({ value, label }) => ({
  value,
  label: `${label} (${value})`,
  chipLabel: value,
}));

const valueTypeCopy: Record<PromoValueType, { label: string; helper: string }> = {
  [PromoValueType.fixed]: {
    label: 'Fixed value',
    helper: 'Fixed value: the promo is worth this flat dollar amount on every quote.',
  },
  [PromoValueType.percentOfCost]: {
    label: 'Percent of system cost',
    helper: 'Percent of system cost: the promo is worth this percentage of the total system design cost.',
  },
  [PromoValueType.perWatt]: {
    label: 'Per watt',
    helper: 'Per watt: the promo is worth this dollar rate multiplied by the system size in watts.',
  },
};

const DEFAULT_VALUE_TYPE_HELPER = 'Choose a value type to see how the value is calculated.';

const valueTypeOrder = [PromoValueType.fixed, PromoValueType.percentOfCost, PromoValueType.perWatt];

const valueTypeOptions = valueTypeOrder.map((value) => ({
  value,
  label: valueTypeCopy[value].label,
}));

const promoTypeCopy: Record<PromoType, { label: string; helper: string }> = {
  [PromoType.incentive]: {
    label: 'Incentive',
    helper: '',
  },
  [PromoType.discount]: {
    label: 'Discount',
    helper: 'Applied to the quote only. No submission or approval step.',
  },
  [PromoType.referralCredit]: {
    label: 'Referral credit',
    helper: 'Applied to the quote only. No submission or approval step.',
  },
};

const DEFAULT_PROMO_TYPE_HELPER = 'Choose a promo type.';

const promoTypeOrder = [PromoType.incentive, PromoType.discount, PromoType.referralCredit];

const promoTypeOptions = promoTypeOrder.map((value) => ({ value, label: promoTypeCopy[value].label }));

const partyOptions = [
  { value: PromoParty.homeowner, label: 'Homeowner' },
  { value: PromoParty.installer, label: 'Certified Installer' },
  { value: PromoParty.financier, label: 'Financier (Palmetto)' },
];

const valueAffixes: Record<PromoValueType, { prefix?: string; suffix?: string }> = {
  [PromoValueType.fixed]: { prefix: '$' },
  [PromoValueType.percentOfCost]: { suffix: '%' },
  [PromoValueType.perWatt]: { prefix: '$', suffix: '/W' },
};

const errorMessage = (text: string | undefined) => (text ? { type: 'error' as const, text } : undefined);

interface FieldLabelProps {
  id?: string;
  children: string;
  required?: boolean;
  optional?: boolean;
}

const FieldLabel = ({ id, children, required, optional }: FieldLabelProps) => (
  <span id={id} className="body-200 text-text-secondary">
    {children}
    {required && <span className="text-text-error"> *</span>}
    {optional && <span className="text-text-tertiary"> (optional)</span>}
  </span>
);

interface PillOptionProps {
  value: string;
  label: string;
  selected: string[];
  onChange: (values: string[]) => void;
  disabled: boolean;
}

const PillOption = ({ value, label, selected, onChange, disabled }: PillOptionProps) => {
  const isSelected = selected.includes(value);
  const handleSelectedChange = useCallback(
    (next: boolean) => onChange(next ? [...selected, value] : selected.filter((entry) => entry !== value)),
    [onChange, selected, value],
  );

  return (
    <PalmettoChip type="button" isSelected={isSelected} onSelectedChange={handleSelectedChange} disabled={disabled}>
      {label}
    </PalmettoChip>
  );
};

interface PillGroupProps {
  id: string;
  label: string;
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onChange: (values: string[]) => void;
  error?: string;
  disabled: boolean;
}

const PillGroup = ({ id, label, options, selected, onChange, error, disabled }: PillGroupProps) => (
  <div role="group" aria-labelledby={`${id}-label`} className="flex flex-col gap-8">
    <FieldLabel id={`${id}-label`} required>
      {label}
    </FieldLabel>
    <div className="flex flex-wrap gap-8">
      {options.map((option) => (
        <PillOption
          key={option.value}
          value={option.value}
          label={option.label}
          selected={selected}
          onChange={onChange}
          disabled={disabled}
        />
      ))}
    </div>
    {error && (
      <div role="alert" className="body-200 text-text-error">
        {error}
      </div>
    )}
  </div>
);

interface PromoModalContentProps {
  promo: PromoRow | null;
  utilities: PromoUtility[];
  onClose: () => void;
}

const PromoModalContent = ({ promo, utilities, onClose }: PromoModalContentProps) => {
  const isEdit = promo !== null;
  const [createPromo, { isLoading: isCreating }] = useCreatePromoMutation();
  const [updatePromo, { isLoading: isUpdating }] = useUpdatePromoMutation();
  const isSaving = isCreating || isUpdating;

  const values = useMemo(() => (promo ? promoToFormValues(promo) : undefined), [promo]);
  const { control, handleSubmit, watch, getValues, setValue } = useForm<PromoFormValues>({
    // `values` applies after the first render; seeding the defaults too keeps the first render from
    // seeing an empty form and the utility sync from dropping the promo's utilities.
    defaultValues: values ?? defaultPromoFormValues,
    values,
  });

  const states = watch('states');
  const valueType = watch('valueType');
  const validFrom = watch('validFrom');
  const promoType = watch('type');
  const isIncentive = promoType === PromoType.incentive;

  const { states: statesWithUtilities, isLoading: isLoadingStates } = usePromoAvailableStates(utilities);
  const stateOptions = useMemo(
    () => allStateOptions.filter(({ value }) => statesWithUtilities.has(value)),
    [statesWithUtilities],
  );

  const {
    options: utilityOptions,
    stateByLseId,
    isLoading: isLoadingUtilities,
  } = usePromoUtilityOptions(states, utilities);

  const visibleUtilityOptions = useMemo(
    () => utilityOptions.filter((option) => states.includes(stateByLseId.get(option.value) ?? '')),
    [utilityOptions, states, stateByLseId],
  );

  const pruneUtilitiesToStates = useCallback(
    (nextStates: string[]) => {
      const current = getValues('lseIds');
      const kept = current.filter((lseId) => nextStates.includes(stateByLseId.get(lseId) ?? ''));
      if (kept.length !== current.length) setValue('lseIds', kept, { shouldDirty: true });
    },
    [getValues, setValue, stateByLseId],
  );

  useEffect(() => {
    if (isLoadingUtilities) return;
    const current = getValues('lseIds');
    const allowed = new Set(visibleUtilityOptions.map((option) => option.value));
    const kept = current.filter((lseId) => allowed.has(lseId));
    if (kept.length !== current.length) setValue('lseIds', kept, { shouldDirty: true });
  }, [visibleUtilityOptions, isLoadingUtilities, getValues, setValue]);

  const onSubmit = useCallback(
    async (formValues: PromoFormValues) => {
      try {
        if (promo) {
          await updatePromo({ id: promo.id, data: toUpdatePromoRequest(formValues, stateByLseId) }).unwrap();
        } else {
          await createPromo(toCreatePromoRequest(formValues, stateByLseId)).unwrap();
        }
        toast.success(promo ? 'Promo updated' : 'Promo created');
        onClose();
      } catch (error) {
        console.error('Error saving promo:', error);
        toast.error(getUserFacingErrorMessage(error, 'Failed to save promo. Please try again.'));
      }
    },
    [promo, createPromo, updatePromo, stateByLseId, onClose],
  );

  const submit = useMemo(() => handleSubmit(onSubmit), [handleSubmit, onSubmit]);

  const bottomContent = (
    <div className="flex justify-end gap-8 border-t border-border-default pt-16">
      <PalmettoButton type="button" emphasis="secondary" layout="hug" onClick={onClose} disabled={isSaving}>
        Cancel
      </PalmettoButton>
      <PalmettoButton type="button" emphasis="primary" layout="hug" onClick={submit} disabled={isSaving}>
        Save promo
      </PalmettoButton>
    </div>
  );

  const affixes = valueType ? valueAffixes[valueType] : {};

  return (
    <PalmettoModal
      className="!max-w-[880px]"
      open
      onClose={onClose}
      invertedBackground={false}
      bottomContent={bottomContent}
    >
      <form className="relative flex flex-col gap-16 pr-24" onSubmit={submit} noValidate>
        <h2 className="border-b border-border-default pb-16 heading-300">{isEdit ? 'Edit promo' : 'New promo'}</h2>

        <div className="grid grid-cols-2 gap-16">
          <Controller
            name="code"
            control={control}
            rules={{ validate: (value) => (isEdit || value.trim() ? undefined : 'Promo code is required') }}
            render={({ field, fieldState }) => (
              <PalmettoTextInput
                id="promo-code"
                label="Promo code"
                placeholder="e.g. SGIP-RES"
                required
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                validationMessage={errorMessage(fieldState.error?.message)}
                disabled={isSaving || isEdit}
              />
            )}
          />

          <Controller
            name="name"
            control={control}
            rules={{ validate: (value) => (value.trim() ? undefined : 'Name is required') }}
            render={({ field, fieldState }) => (
              <PalmettoTextInput
                id="promo-name"
                label="Promo name"
                placeholder="e.g. SGIP Residential"
                required
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                validationMessage={errorMessage(fieldState.error?.message)}
                disabled={isSaving}
              />
            )}
          />
        </div>

        <Controller
          name="type"
          control={control}
          rules={{ validate: (value) => (isEdit ? undefined : validatePromoType(value)) }}
          render={({ field, fieldState }) => (
            <PalmettoSelect
              id="promo-type"
              label="Promo type"
              placeholder="Select a promo type..."
              required
              items={promoTypeOptions.map((option) => ({ ...option, selected: option.value === field.value }))}
              onValueChange={(details: { value: string[] }) => field.onChange(details.value[0] ?? '')}
              helperText={field.value ? promoTypeCopy[field.value].helper || undefined : DEFAULT_PROMO_TYPE_HELPER}
              validationMessage={errorMessage(fieldState.error?.message)}
              disabled={isSaving || isEdit}
            />
          )}
        />

        <div className="grid grid-cols-2 gap-16">
          <Controller
            name="states"
            control={control}
            rules={{ validate: validateStates, deps: ['lseIds'] }}
            render={({ field, fieldState }) => (
              <SearchableMultiSelect
                id="promo-states"
                label="State (select any or all)"
                placeholder="Select states..."
                searchPlaceholder="Search states..."
                emptyMessage="No states found"
                allSelectedLabel="All states"
                options={stateOptions}
                loading={isLoadingStates}
                value={field.value}
                onChange={(next) => {
                  field.onChange(next);
                  pruneUtilitiesToStates(next);
                }}
                validationMessage={errorMessage(fieldState.error?.message)}
                disabled={isSaving}
              />
            )}
          />

          <Controller
            name="lseIds"
            control={control}
            rules={{ validate: (value, formValues) => validateLseIds(value, formValues.states, stateByLseId) }}
            render={({ field, fieldState }) => (
              <SearchableMultiSelect
                id="promo-utilities"
                label="Utility (select any or all)"
                placeholder={states.length === 0 ? 'Select a state first' : 'Select utilities...'}
                searchPlaceholder="Search utilities..."
                emptyMessage="No utilities found"
                allSelectedLabel="All utilities"
                options={visibleUtilityOptions}
                value={field.value}
                onChange={field.onChange}
                loading={isLoadingUtilities}
                validationMessage={errorMessage(fieldState.error?.message)}
                disabled={isSaving}
              />
            )}
          />
        </div>

        <div className="flex flex-col gap-8">
          <div className="grid grid-cols-2 gap-16">
            <Controller
              name="valueType"
              control={control}
              rules={{ validate: (value) => (isEdit ? undefined : validateValueType(value)), deps: ['value'] }}
              render={({ field, fieldState }) => (
                <PalmettoSelect
                  id="promo-value-type"
                  label="Value type"
                  placeholder="Select a value type..."
                  required
                  items={valueTypeOptions.map((option) => ({ ...option, selected: option.value === field.value }))}
                  onValueChange={(details: { value: string[] }) => field.onChange(details.value[0] ?? '')}
                  validationMessage={errorMessage(fieldState.error?.message)}
                  disabled={isSaving || isEdit}
                />
              )}
            />

            <Controller
              name="value"
              control={control}
              rules={{
                validate: (value, formValues) => (isEdit ? undefined : validateValue(value, formValues.valueType)),
                deps: ['valueType'],
              }}
              render={({ field, fieldState }) => (
                <PalmettoTextInput
                  id="promo-value"
                  label="Value"
                  placeholder="Enter value"
                  required
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="any"
                  prefix={affixes.prefix}
                  suffix={affixes.suffix}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  validationMessage={errorMessage(fieldState.error?.message)}
                  disabled={isSaving || isEdit}
                />
              )}
            />
          </div>
          <p className="body-200 text-text-secondary">
            {valueType ? valueTypeCopy[valueType].helper : DEFAULT_VALUE_TYPE_HELPER}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-16">
          <Controller
            name="validFrom"
            control={control}
            rules={{ validate: (value) => (isEdit || value ? undefined : 'Valid from is required') }}
            render={({ field, fieldState }) => {
              const selected = fromIsoDateString(field.value);
              return (
                <DateInput
                  id="promo-valid-from"
                  name="promo-valid-from"
                  label="Valid from"
                  placeholder="MM-DD-YYYY"
                  value={selected ? selected.toLocaleDateString('en-US') : ''}
                  isRequired
                  error={fieldState.error?.message}
                  isDisabled={isSaving || isEdit}
                  datePickerProps={{
                    onChange: (date: Date | [Date, Date] | null) => {
                      const picked = Array.isArray(date) ? date[0] : date;
                      field.onChange(picked ? toIsoDateString(picked) : '');
                    },
                    selected,
                  }}
                  textInputProps={{}}
                />
              );
            }}
          />

          <Controller
            name="validThrough"
            control={control}
            rules={{
              validate: (value, formValues) =>
                validateValidThrough(value, formValues.validFrom, { hadValidThrough: Boolean(promo?.validThrough) }),
              deps: ['validFrom'],
            }}
            render={({ field, fieldState }) => {
              const selected = fromIsoDateString(field.value);
              return (
                <DateInput
                  id="promo-valid-through"
                  name="promo-valid-through"
                  label="Valid through (optional)"
                  placeholder="MM-DD-YYYY"
                  value={selected ? selected.toLocaleDateString('en-US') : ''}
                  error={fieldState.error?.message}
                  isDisabled={isSaving}
                  datePickerProps={{
                    onChange: (date: Date | [Date, Date] | null) => {
                      const picked = Array.isArray(date) ? date[0] : date;
                      field.onChange(picked ? toIsoDateString(picked) : '');
                    },
                    selected,
                    minDate: fromIsoDateString(validFrom) ?? null,
                  }}
                  textInputProps={{}}
                />
              );
            }}
          />
        </div>

        {isIncentive && (
          <Controller
            name="collectableBy"
            control={control}
            rules={{
              validate: (value, formValues) =>
                isEdit ? undefined : validateCollectableBy(value, formValues.type === PromoType.incentive),
            }}
            render={({ field, fieldState }) => (
              <PillGroup
                id="promo-collectable-by"
                label="Who can obtain it (select any or all)"
                options={partyOptions}
                selected={field.value}
                onChange={field.onChange}
                error={fieldState.error?.message}
                disabled={isSaving || isEdit}
              />
            )}
          />
        )}

        <Controller
          name="programTypes"
          control={control}
          rules={{ validate: (value) => (isEdit ? undefined : validateProgramTypes(value)) }}
          render={({ field, fieldState }) => (
            <PillGroup
              id="promo-program-types"
              label="Applies to program types (multi-select)"
              options={programTypeOptions}
              selected={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
              disabled={isSaving || isEdit}
            />
          )}
        />
      </form>
    </PalmettoModal>
  );
};

export interface PromoModalProps {
  open: boolean;
  promo: PromoRow | null;
  /** Utility lookup from the list response, so edit mode can name the promo's current utilities. */
  utilities: PromoUtility[];
  onClose: () => void;
}

export const PromoModal = ({ open, promo, utilities, onClose }: PromoModalProps) => {
  if (!open) return null;

  // Remounting per open and per promo starts every form from a clean state.
  return <PromoModalContent key={promo?.id ?? 'create'} promo={promo} utilities={utilities} onClose={onClose} />;
};
